import { type ElementType, type ReactNode } from "react";
import {
    ArrowsRightLeftIcon,
    BanknotesIcon,
    CreditCardIcon,
    CurrencyDollarIcon,
    WalletIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";

import { getWalletStats } from "@/features/dashboard/api/dashboardApi";

const fmtCurrency = (amount: number | null | undefined) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(amount);
};

const fmtNumber = (value: number | null | undefined) =>
    value == null ? "—" : new Intl.NumberFormat("en-NG").format(value);

const StatCard = ({
    icon: Icon,
    label,
    value,
    subtitle,
}: {
    icon: ElementType;
    label: string;
    value: ReactNode;
    subtitle?: string;
}) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-50 p-2 dark:bg-slate-800/60">
                <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">{value}</p>
            </div>
        </div>
        {subtitle ? <p className="mt-2 text-xs text-slate-400">{subtitle}</p> : null}
    </div>
);

export const FinancialAnalyticsRoute = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ["walletStats"],
        queryFn: () => getWalletStats(),
    });

    const stats = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
                    Failed to load financial analytics
                </p>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
                <p className="text-sm text-slate-500 dark:text-slate-400">No financial data available.</p>
            </div>
        );
    }

    const totalWalletFunded = stats.totalWalletFunded ?? stats.totalTopUpVolume ?? 0;
    const platformCommission = stats.platformCommission ?? stats.totalCommissions ?? 0;
    const avgRidePrice = stats.avgRidePrice ?? null;
    const walletBalances = stats.walletBalances ?? stats.totalBalanceInCirculation ?? 0;
    const pendingDriverPayoutAmount = stats.pendingDriverPayoutAmount ?? 0;
    const completedPayoutAmount = stats.completedPayoutAmount ?? stats.totalPayoutAmount ?? 0;
    const totalWalletFundedWtd = stats.totalWalletFundedWtd ?? stats.topUpVolumeLast7Days ?? 0;
    const platformCommissionWtd = stats.platformCommissionWtd ?? stats.commissionsLast7Days ?? 0;
    const totalPayoutWtd = stats.totalPayoutWtd ?? stats.payoutsLast7Days ?? 0;

    return (
        <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    icon={WalletIcon}
                    label="Total Wallet Funded"
                    value={fmtCurrency(totalWalletFunded)}
                    subtitle="Completed top-up wallet transactions"
                />
                <StatCard
                    icon={CurrencyDollarIcon}
                    label="Platform Commission"
                    value={fmtCurrency(platformCommission)}
                    subtitle="Commission already posted to wallet transactions"
                />
                <StatCard
                    icon={BanknotesIcon}
                    label="Wallet Balances"
                    value={fmtCurrency(walletBalances)}
                    subtitle="Money currently sitting in active wallets"
                />
                <StatCard
                    icon={ArrowsRightLeftIcon}
                    label="Pending Driver Payouts"
                    value={fmtCurrency(pendingDriverPayoutAmount)}
                    subtitle="Current payout liability"
                />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">Week to Date</h3>
                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Wallet Funded WTD</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                            {fmtCurrency(totalWalletFundedWtd)}
                        </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Commission WTD</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                            {fmtCurrency(platformCommissionWtd)}
                        </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Payout WTD</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                            {fmtCurrency(totalPayoutWtd)}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <StatCard
                    icon={CreditCardIcon}
                    label="Avg Ride Price"
                    value={fmtCurrency(avgRidePrice)}
                    subtitle="Average confirmed booking fare"
                />
                <StatCard
                    icon={BanknotesIcon}
                    label="Completed Payouts"
                    value={fmtCurrency(completedPayoutAmount)}
                    subtitle="Money already paid out to drivers"
                />
                <StatCard
                    icon={WalletIcon}
                    label="Total Wallets"
                    value={fmtNumber(stats.totalWallets)}
                    subtitle={`${fmtNumber(stats.activeWallets)} active wallets`}
                />
            </div>
        </div>
    );
};
